import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { useCredits } from '../contexts/CreditContext';
import { creditsService } from '../services/credits';
import { brandQueriesApi } from '../services/brand-queries';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import BrandsSettings from '../components/settings/BrandsSettings';
import AgencyBrandingSettings from '../components/settings/AgencyBrandingSettings';
import NotificationsSettings from '../components/settings/NotificationsSettings';
import WebhooksIntegration from '../components/settings/WebhooksIntegration';
import McpIntegration from '../components/settings/McpIntegration';
import ApiIntegration from '../components/settings/ApiIntegration';
import BillingSettings from '../components/settings/BillingSettings';
import { resolveLimits, resolveEntitlement, canAccessReports, effectiveFeatureFlags } from '@traaker/shared';
import type {
  User,
  UserRole,
  Tenant,
  TierLimits,
} from '@traaker/shared';

const ROLE_LABEL_KEYS: Record<UserRole, string> = {
  owner: 'settings.role.owner',
  admin: 'settings.role.admin',
  member: 'settings.role.member',
  viewer: 'settings.role.viewer',
};

const ROLE_COLORS: Record<UserRole, string> = {
  owner: '#10B981',
  admin: '#7C3AED',
  member: '#5F6470',
  viewer: '#A0A4AE',
};

const TAB_KEYS = ['org', 'brands', 'users', 'billing', 'integrations', 'branding', 'notifications'] as const;
type TabKey = (typeof TAB_KEYS)[number];

/** Tabs rendered in the strip, in order. `billing` is deliberately ABSENT: the
 *  Facturation panel is its own sidebar section, so it renders standalone (no
 *  strip at all — see `isBillingPanel`) and a tab here would be a second door.
 *  `?tab=billing` still RESOLVES because it stays in `TAB_KEYS` — keep the two
 *  lists separate, or the sidebar link falls back to the Entreprise tab. */
const STRIP_TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'org', labelKey: 'settings.tab.org' },
  { key: 'brands', labelKey: 'settings.tab.brands' },
  { key: 'users', labelKey: 'settings.tab.users' },
  { key: 'notifications', labelKey: 'settings.tab.notifications' },
  { key: 'integrations', labelKey: 'settings.tab.integrations' },
];

export default function Settings() {
  const { t } = useTranslation();
  const { tenant, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<User[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  // White-label tab follows the Reports access rule: agency always, enterprise
  // via the reportsEnabled flag.
  const reportsEnabled = canAccessReports(tenant?.tier, tenant?.settings?.featureFlags?.reportsEnabled);
  // MCP + public REST API access: ON on every tier since pricing v2 D4
  // (2026-11-04) unless admin-overridden per tenant via quotaOverrides.webhookAccess.
  // One gate for both surfaces — see docs/platform/mcp-server.md.
  const mcpEnabled = !!tenant && resolveLimits(tenant.tier, tenant.settings?.quotaOverrides).webhookAccess;
  const conversationsEnabled = !!tenant && !!effectiveFeatureFlags(tenant.tier, tenant.settings?.featureFlags).conversationsEnabled;

  const tabParam = (searchParams.get('tab') || 'org') as TabKey;
  const strip = reportsEnabled
    ? [...STRIP_TABS, { key: 'branding' as TabKey, labelKey: 'settings.tab.branding' }]
    : STRIP_TABS;
  // `billing` resolves but isn't in the strip, so an unknown/hidden tab falls
  // back to `org` while a valid one is honoured verbatim.
  const activeTab: TabKey = TAB_KEYS.includes(tabParam) && (tabParam !== 'branding' || reportsEnabled)
    ? tabParam
    : 'org';
  // Facturation renders standalone (own sidebar entry): no strip, own H5 title.
  const isBillingPanel = activeTab === 'billing';
  const activeIdx = Math.max(0, strip.findIndex((tb) => tb.key === activeTab));

  const handleTabChange = (_: unknown, value: number) => {
    const key = strip[value].key;
    setSearchParams(key === 'org' ? {} : { tab: key });
  };

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  // Seats shown on the Users tab title (« Utilisateurs (2/5) ») — the registry gauge
  // the invite path enforces (tier default + packs + override). Moved here from the
  // Organisation quota card (2026-11-04).
  const usersLimit = tenant
    ? resolveEntitlement('users', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.users,
        packs: tenant.settings?.activePacks?.users,
      }).limit
    : -1;

  useEffect(() => {
    api.get<User[]>('/users').then(setUsers).catch(() => {});
  }, []);

  const handleRoleChange = async (userId: string, role: UserRole) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, role } : u)));
    } catch {
      setMessage(t('settings.error.roleChange'));
    }
  };

  const handleRemoveUser = async (userId: string) => {
    try {
      await api.delete(`/users/${userId}`);
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch {
      setMessage(t('settings.error.userRemove'));
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        {t(isBillingPanel ? 'settings.tab.billing' : 'settings.title')}
      </Typography>

      {message && (
        <Alert severity="info" onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {/* Facturation is its own sidebar section, so it renders standalone — no
          tab strip of unrelated Settings panels above it. */}
      {!isBillingPanel && (
        <Tabs
          value={activeIdx}
          onChange={handleTabChange}
          sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {strip.map((tb) => (
            <Tab key={tb.key} label={t(tb.labelKey)} />
          ))}
        </Tabs>
      )}

      {/* Tab: Organization */}
      {activeTab === 'org' && tenant && <EnterpriseTab tenant={tenant} />}

      {/* Tab: Brands & Locations */}
      {activeTab === 'brands' && <BrandsSettings />}

      {/* Tab: Billing (subscribe / upgrade / packs / credits / manage) */}
      {activeTab === 'billing' && tenant && <BillingSettings tenant={tenant} />}

      {/* Tab: Notifications (registry-driven email notifications) */}
      {activeTab === 'notifications' && <NotificationsSettings />}

      {/* Tab: Integrations (webhooks; WordPress + RSS live in the Studio) */}
      {activeTab === 'integrations' && (
        <>
          <WebhooksIntegration />
          <ApiIntegration entitled={mcpEnabled} conversationsEnabled={conversationsEnabled} />
          <McpIntegration entitled={mcpEnabled} conversationsEnabled={conversationsEnabled} />
        </>
      )}

      {/* Tab: Agency branding (reports white-label) */}
      {activeTab === 'branding' && reportsEnabled && <AgencyBrandingSettings />}

      {/* Tab: Users */}
      {activeTab === 'users' && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {usersLimit > 0
                  ? t('settings.users.titleWithLimit', { count: users.length, limit: usersLimit })
                  : t('settings.users.title', { count: users.length })}
              </Typography>
              {isAdmin && (
                <Button
                  startIcon={<PersonAddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setInviteOpen(true)}
                >
                  {t('settings.users.invite')}
                </Button>
              )}
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('settings.users.name')}</TableCell>
                  <TableCell>{t('settings.users.email')}</TableCell>
                  <TableCell>{t('settings.users.role')}</TableCell>
                  {isAdmin && <TableCell align="right">{t('settings.users.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {isAdmin && u.role !== 'owner' ? (
                        <Select
                          value={u.role}
                          size="small"
                          onChange={(e) => handleRoleChange(u.userId, e.target.value as UserRole)}
                          sx={{ minWidth: 130 }}
                        >
                          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
                            <MenuItem key={r} value={r}>{t(ROLE_LABEL_KEYS[r])}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          label={t(ROLE_LABEL_KEYS[u.role])}
                          size="small"
                          sx={{ bgcolor: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role], fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        {u.role !== 'owner' && (
                          <IconButton size="small" onClick={() => handleRemoveUser(u.userId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(u) => {
          setUsers((prev) => [...prev, u]);
          setInviteOpen(false);
        }}
      />
    </Box>
  );
}

// Quota metadata: label + plain-French explanation. `usage` (when set) names a
// live-count source so the row renders a gauge instead of a bare number.
type UsageKey = 'brands' | 'competitors';
interface QuotaMeta {
  key: keyof TierLimits;
  labelKey: string;
  descKey: string;
  usage?: UsageKey;
}

// Headline quotas — only the commercial gauges left after pricing v2 (2026-11-04).
// Zones, tasks and the legacy `prompts` gauge are no longer limits; prompt
// requests + conversation runs are shown as usage bars in the header.
const QUOTA_PRIMARY: ReadonlyArray<QuotaMeta> = [
  { key: 'brands', labelKey: 'settings.quota.brands.label', descKey: 'settings.quota.brands.desc', usage: 'brands' },
  { key: 'competitors', labelKey: 'settings.quota.competitors.label', descKey: 'settings.quota.competitors.desc', usage: 'competitors' },
];

// Brand-perception prompt budget — ONE shared gauge across the 3 axes
// (narrative/authority/referral). Replaced the legacy per-axis rows.

/**
 * Headline quota with a usage gauge. For finite limits with a live `used` count
 * (non-agency), renders a labeled progress bar (amber ≥80%, red at 100%); for
 * unlimited (-1, e.g. agency) renders an "Illimité" pill. Always shows the
 * one-line explanation so the quota is self-describing.
 */
function QuotaGauge({
  meta, limit, used, overridden, agency,
}: {
  meta: { label: string; desc: string; usage?: string };
  limit: number;
  used?: number;
  overridden: boolean;
  agency: boolean;
}) {
  const { t } = useTranslation();
  const unlimited = limit === -1;
  const showBar = !unlimited && !agency && typeof used === 'number';
  const pct = showBar && limit > 0 ? Math.min(100, Math.round((used! / limit) * 100)) : 0;
  const color = pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
          {overridden && <Chip label={t('settings.quota.custom')} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
        </Box>
        <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {unlimited
            ? t('settings.quota.unlimited')
            : showBar
              ? <>{used}<Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}> / {limit}</Box></>
              : limit}
        </Typography>
      </Box>
      {showBar && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={color}
          sx={{ height: 6, borderRadius: 99, mt: 0.75, bgcolor: 'action.hover' }}
        />
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {meta.desc}
      </Typography>
    </Box>
  );
}

/**
 * « Consommation du mois » (pricing v2, 2026-11-04): what was actually SPENT this
 * month — credits against the monthly allocation (every tier with one), and the
 * prompt requests / conversation runs used against each workspace's allowance
 * (non-agency; allowances are per brand today, so one gauge per workspace). The
 * header bars show the configuration's monthly projection instead.
 */
function MonthlyConsumption({ tier }: { tier: string }) {
  const { t } = useTranslation();
  const { brands } = useBrand();
  const { balance } = useCredits();
  const isAgency = tier === 'agency';
  const [creditsUsed, setCreditsUsed] = useState<number | null>(null);
  const [byBrand, setByBrand] = useState<
    Array<{ brandId: string; name: string; requests: { used: number; allowance: number }; conversations: { used: number; allowance: number } }>
  >([]);

  useEffect(() => {
    creditsService.getUsage().then((u) => setCreditsUsed(u.total)).catch(() => setCreditsUsed(null));
  }, []);
  useEffect(() => {
    if (isAgency || brands.length === 0) return;
    let alive = true;
    Promise.all(
      brands.map((b) =>
        brandQueriesApi
          .allowances(b.brandId)
          .then((a) => ({ brandId: b.brandId, name: b.name, requests: a.promptRequests, conversations: a.conversationRuns }))
          .catch(() => null),
      ),
    ).then((rows) => {
      if (alive) setByBrand(rows.filter((r): r is NonNullable<typeof r> => r !== null));
    });
    return () => {
      alive = false;
    };
  }, [isAgency, brands]);

  const allocation = balance?.monthlyAllocation ?? 0;
  const multi = byBrand.length > 1;
  const gauges: Array<{ key: string; label: string; desc: string; used: number; limit: number }> = [];
  if (allocation > 0 && creditsUsed !== null) {
    gauges.push({
      key: 'credits',
      label: t('settings.consumption.credits.label'),
      desc: t(isAgency ? 'settings.consumption.credits.descAgency' : 'settings.consumption.credits.desc'),
      used: creditsUsed,
      limit: allocation,
    });
  }
  for (const b of byBrand) {
    if (b.requests.allowance > 0) {
      gauges.push({
        key: `req-${b.brandId}`,
        label: multi ? t('settings.consumption.requests.labelBrand', { brand: b.name }) : t('settings.consumption.requests.label'),
        desc: t('settings.consumption.requests.desc'),
        used: b.requests.used,
        limit: b.requests.allowance,
      });
    }
    if (b.conversations.allowance > 0) {
      gauges.push({
        key: `conv-${b.brandId}`,
        label: multi ? t('settings.consumption.conversations.labelBrand', { brand: b.name }) : t('settings.consumption.conversations.label'),
        desc: t('settings.consumption.conversations.desc'),
        used: b.conversations.used,
        limit: b.conversations.allowance,
      });
    }
  }
  if (gauges.length === 0) return null;

  return (
    <>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>
        {t('settings.consumption.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {gauges.map((g) => (
          <QuotaGauge
            key={g.key}
            meta={{ label: g.label, desc: g.desc }}
            limit={g.limit}
            used={g.used}
            overridden={false}
            agency={false}
          />
        ))}
      </Box>
    </>
  );
}

function EnterpriseTab({ tenant }: { tenant: Tenant }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brands } = useBrand();
  // Live tenant-wide usage (competitors + prompts have no client-side count) —
  // ONE call, refreshed when the brand list changes. Null until loaded / on error.
  const [usedByMetric, setUsedByMetric] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ metrics: { metric: string; used: number }[] }>('/metering/usage')
      .then((u) => {
        if (cancelled) return;
        setUsedByMetric(Object.fromEntries((u.metrics ?? []).map((m) => [m.metric, m.used])));
      })
      .catch(() => { if (!cancelled) setUsedByMetric({}); });
    return () => { cancelled = true; };
  }, [brands.length]);
  const limits = resolveLimits(tenant.tier, tenant.settings?.quotaOverrides);
  const overrideKeys = Object.keys(tenant.settings?.quotaOverrides ?? {}) as Array<keyof TierLimits>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Identity — read only */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {t('settings.enterprise.title')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('settings.enterprise.managedInfo')}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label={t('settings.enterprise.nameLabel')}
              value={tenant.name}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
            <TextField
              label={t('settings.enterprise.domainLabel')}
              value={tenant.domain}
              disabled
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Abonnement (offre, statut, actions Changer d'offre / Gérer
          l'abonnement) vit entièrement dans la page Facturation
          (settings?tab=billing, entrée dédiée dans le menu — plus d'onglet
          ici) — pas de duplication ici. */}

      {/* Quotas & usage */}
      {(() => {
        const isAgency = tenant.tier === 'agency';
        const usageValue: Record<UsageKey, number | undefined> = {
          brands: brands.length,
          competitors: usedByMetric.competitors,
        };
        return (
          <Card data-tour="quotas-card">
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('settings.quotas.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {isAgency
                  ? t('settings.quotas.agencyDesc')
                  : t('settings.quotas.desc')}
              </Typography>

              {/* Headline quotas — gauges where we have a live count. */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {QUOTA_PRIMARY.map((row) => (
                  <QuotaGauge
                    key={row.key}
                    meta={{ label: t(row.labelKey), desc: t(row.descKey), usage: row.usage }}
                    limit={limits[row.key] as number}
                    used={row.usage ? usageValue[row.usage] : undefined}
                    overridden={overrideKeys.includes(row.key)}
                    agency={isAgency}
                  />
                ))}
              </Box>

              <MonthlyConsumption tier={tenant.tier} />
            </CardContent>
          </Card>
        );
      })()}

      {/* Product tour replay */}
      <Card>
        <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('settings.tour.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings.tour.desc')}
            </Typography>
          </Box>
          <Button
            startIcon={<HelpOutlineIcon />}
            variant="outlined"
            onClick={() => navigate('/', { state: { replayTour: true } })}
          >
            {t('settings.tour.replay')}
          </Button>
        </CardContent>
      </Card>

    </Box>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (user: User) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    try {
      const user = await api.post<User>('/users/invite', { email, name, role });
      onInvited(user);
      setEmail('');
      setName('');
      setRole('member');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('settings.invite.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label={t('settings.users.name')} value={name} onChange={(e) => setName(e.target.value)} fullWidth />
        <TextField label={t('settings.users.email')} value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
        <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)} fullWidth>
          {(['admin', 'member', 'viewer'] as UserRole[]).map((r) => (
            <MenuItem key={r} value={r}>{t(ROLE_LABEL_KEYS[r])}</MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('settings.invite.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!email || !name}>
          {t('settings.invite.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
